import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../pages/app/protected/firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export default function OnboardingGuide() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [showGuide, setShowGuide] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [completedSteps, setCompletedSteps] = useState({
    account: false,
    profile: false,
    settings: false,
    tour: false,
    help: false
  });
  const [companyId, setCompanyId] = useState('');
  const [showWelcome, setShowWelcome] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [isSidebarTour, setIsSidebarTour] = useState(false);
  const [currentSidebarItem, setCurrentSidebarItem] = useState(0);
  const [validationError, setValidationError] = useState('');
  const [isValidatingStep, setIsValidatingStep] = useState(false);

  const steps = [
    {
      key: 'account',
      title: 'Set up your Account',
      description: 'Complete your personal information',
      path: '/my-account',
      icon: '👤'
    },
    {
      key: 'profile',
      title: 'Company Profile',
      description: 'Add your company details and logo',
      path: '/company-profile',
      icon: '🏢'
    },
    {
      key: 'settings',
      title: 'Company Settings',
      description: 'Configure your company preferences',
      path: '/company-settings',
      icon: '⚙️'
    },
    {
      key: 'tour',
      title: 'Take a Tour',
      description: 'Learn how to navigate the app',
      path: '#',
      icon: '🚀'
    },
    {
      key: 'help',
      title: 'Need Help?',
      description: 'Learn how to get the most out of CashMate',
      path: '#',
      icon: '❓'
    }
  ];

  const sidebarItems = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      description: 'Here you can view your business overview with key metrics and quick access to important information. Total revenue calculated based on how many money you have made throughout using CashMate, profit card will remain green if your business is in profit and will turn red if your business is in loss. Goal card will remain green if you achived your sales target for the day and turn red if you haven\'t achived yet. Total customer calculated based on how many order recieved.',
      path: '/dashboard'
    },
    { 
      id: 'inventory', 
      label: 'Inventory', 
      description: 'Here you can manage your products and bundles/sets to cater to your needs.',
      path: '/inventory'
    },
    { 
      id: 'cashier', 
      label: 'Cashier', 
      description: 'Here you can start selling and manage transactions. Note that if you want to sell pre-order items, you can enabe pre-order settings on cart straight away. Pre-order list are displayed on kitchen display.',
      path: '/product-view'
    },
    {
      id: 'online', 
      label: 'Online Order', 
      description: 'Here you can manage your orders and pre-order list made from online ordering.',
      path: '/online'
    },
    {
      id: 'history', 
      label: 'History', 
      description: 'Here you can view your business history and perform transaction like view receipt, share receipt and void receipt. Any voided receipt will add back the quantity that were recorded in the receipts and for receipt sharing you can either send link or re-print the receipts.',
      path: '/history'
    },
    { 
      id: 'reports', 
      label: 'Reports', 
      description: 'Here you can analyze your business reports and analytics. You can generate reports for different time periods and view them in a clear and easy-to-understand format.',
      path: '/reports'
    },
    {
      id: 'accounting', 
      label: 'Accounting', 
      description: 'In accounting section, you can record journal, money in and money out apart from the cashier menu. ',
      path: '/accounting'
    },
    {
      id: 'filemanager', 
      label: 'File Manager', 
      description: 'Here you can manage your files and documents. You can delete files and documents here.',
      path: '/file-manager'
    },
    {
      id: 'backup', 
      label: 'Backup', 
      description: 'Here you can backup your data. You can extract your data to Excel and export it. This feature is currently under development and will be available soon.',
      path: '/backup'
    },
    {
      id: 'billing', 
      label: 'Billing & Subscription', 
      description: 'Here you can manage your billing subscription, upgrade or downgrade your plan and review your subscription invoice.',
      path: '/billing'
    },
  ];

  const excludedPaths = ['/my-account', '/company-profile', '/company-settings'];
  const isExcludedPath = excludedPaths.some(path => location.pathname.includes(path));

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user?.uid) return;
      
      try {
        // First check if onboarding is already completed
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const isOnboardingCompleted = userDoc.data()?.onboardingCompleted === true;
        
        if (isOnboardingCompleted) {
          // If onboarding is completed, hide everything
          setShowWelcome(false);
          setShowGuide(false);
          setIsSidebarTour(false);
          setDismissed(true);
          return;
        }
        
        // If not completed, continue with normal flow
        const [accountDoc, profileDoc, settingsDoc] = await Promise.all([
          getDoc(doc(db, 'companies', userDoc.data()?.companyId, 'members', user.uid)),
          getDoc(doc(db, 'companies', userDoc.data()?.companyId, 'companyProfile', 'info')),
          getDoc(doc(db, 'companies', userDoc.data()?.companyId, 'companySettings', 'info'))
        ]);

        const isNewUser = !accountDoc.exists() && !profileDoc.exists() && !settingsDoc.exists();
        setIsNewUser(isNewUser);
        
        if (isNewUser) {
          setShowWelcome(true);
        }
        
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      }
    };

    checkOnboardingStatus();
  }, [user]);

  const handleWelcomeNext = () => {
    setShowWelcome(false);
    setShowGuide(true);
  };

  const handleNextSidebarItem = () => {
    const nextItemIndex = currentSidebarItem + 1;
    
    if (nextItemIndex < sidebarItems.length) {
      const nextItem = sidebarItems[nextItemIndex];
      navigate(nextItem.path);
      setCurrentSidebarItem(nextItemIndex);
    } else {
      const newCompleted = { ...completedSteps, tour: true };
      setCompletedSteps(newCompleted);
      setIsSidebarTour(false);
      setCurrentStep(steps.findIndex(step => step.key === 'help'));
      localStorage.removeItem('cashmate_tour_state'); // Clear saved state when tour is completed
    }
  };

  const handleCompleteStep = async (step) => {
    setValidationError('');

    if (step.key === 'tour') {
      // Set tour state immediately to prevent welcome note
      setIsSidebarTour(true);
      // Then navigate to dashboard
      navigate('/dashboard');
      return;
    }

    if (step.key === 'help') {
      await markOnboardingCompleted();
      return;
    }

    if (['account', 'profile', 'settings'].includes(step.key)) {
      setIsValidatingStep(true);
      const isStepComplete = await checkStepCompletion(step.key, true);
      setIsValidatingStep(false);

      // Always navigate to the relevant page to encourage completion
      navigate(step.path);

      if (!isStepComplete) {
        return;
      }
    } else {
      navigate(step.path);
    }

    const newCompleted = { ...completedSteps, [step.key]: true };
    setCompletedSteps(newCompleted);
    
    // Move to next incomplete step
    const nextStep = steps.findIndex(s => !newCompleted[s.key]);
    if (nextStep >= 0) {
      setCurrentStep(nextStep);
    }
  };

  const renderSidebarTour = () => {
    const currentItem = sidebarItems[currentSidebarItem];
    
    return (
      <div 
        className="fixed bottom-6 right-6 z-50 w-96 bg-gray-800 p-6 rounded-xl shadow-2xl border border-gray-700"
      >
        <h3 className="font-semibold text-xl text-white mb-2">{currentItem.label}</h3>
        <p className="text-sm text-gray-300 mb-6">{currentItem.description}</p>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-400">
              {currentSidebarItem + 1} of {sidebarItems.length}
            </span>
            <div className="text-xs text-purple-400">
              {currentItem.path}
            </div>
          </div>
          
          <div className="space-x-2">
            {currentSidebarItem < sidebarItems.length - 1 ? (
              <button
                onClick={handleNextSidebarItem}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
              >
                Next: {sidebarItems[currentSidebarItem + 1]?.label}
              </button>
            ) : (
              <button
                onClick={() => {
                  const newCompleted = { ...completedSteps, tour: true };
                  setCompletedSteps(newCompleted);
                  setIsSidebarTour(false);
                  setCurrentStep(steps.findIndex(step => step.key === 'help'));
                  setShowGuide(true);
                  navigate('/billing')
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                Finish Tour
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const fetchCompanyId = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists() && userDoc.data().companyId) {
          const companyId = userDoc.data().companyId;
          setCompanyId(companyId);
        }
      } catch (error) {
        console.error('Error fetching company ID:', error);
      }
    };

    fetchCompanyId();
  }, [user, location.pathname, dismissed]);

  const checkStepCompletion = async (stepKey, showError = false) => {
    if (!user?.uid || !companyId) return false;

    const setErrorMessage = (message) => {
      if (showError) {
        setValidationError(message);
      }
    };

    try {
      if (stepKey === 'account') {
        const accountDoc = await getDoc(doc(db, 'companies', companyId, 'members', user.uid));
        const data = accountDoc.data() || {};
        const requiredFields = ['firstName', 'lastName', 'phone'];
        const missing = requiredFields.filter(field => !data[field]);

        if (missing.length > 0) {
          setErrorMessage('Please complete and save your account details (first name, last name and phone).');
          return false;
        }
        return true;
      }

      if (stepKey === 'profile') {
        const profileDoc = await getDoc(doc(db, 'companies', companyId, 'companyProfile', 'info'));
        const data = profileDoc.data() || {};
        const requiredFields = ['companyName', 'regNumber', 'countryCode', 'phone', 'email', 'street', 'postcode', 'city', 'state', 'country'];
        const missing = requiredFields.filter(field => !data[field]);

        if (missing.length > 0) {
          setErrorMessage('Please fill in and save all required company profile fields before continuing.');
          return false;
        }
        return true;
      }

      if (stepKey === 'settings') {
        const settingsDoc = await getDoc(doc(db, 'companies', companyId, 'companySettings', 'info'));
        const data = settingsDoc.data() || {};

        if (!data.thankYouNote) {
          setErrorMessage('Please save your company settings (thank you note is required) before continuing.');
          return false;
        }
        return true;
      }

      return true;
    } catch (error) {
      console.error('Error validating onboarding step:', error);
      setErrorMessage('Unable to verify this step right now. Please try again.');
      return false;
    }
  };

  useEffect(() => {
    const syncCompletedSteps = async () => {
      if (!companyId || !user?.uid) return;

      const [accountComplete, profileComplete, settingsComplete] = await Promise.all([
        checkStepCompletion('account'),
        checkStepCompletion('profile'),
        checkStepCompletion('settings')
      ]);

      setCompletedSteps(prev => {
        const updated = {
          ...prev,
          account: accountComplete,
          profile: profileComplete,
          settings: settingsComplete
        };

        const nextStepIndex = steps.findIndex(step => !updated[step.key]);
        if (nextStepIndex >= 0 && nextStepIndex !== currentStep) {
          setCurrentStep(nextStepIndex);
        }

        return updated;
      });
    };

    syncCompletedSteps();
  }, [companyId, user?.uid, location.pathname, currentStep]);

  useEffect(() => {
    setValidationError('');
  }, [currentStep]);

  useEffect(() => {
    const savedTourState = localStorage.getItem('cashmate_tour_state');
    if (savedTourState) {
      const { currentItemIndex, completedSteps: savedCompletedSteps } = JSON.parse(savedTourState);
      setCurrentSidebarItem(currentItemIndex);
      setCompletedSteps(prev => ({
        ...prev,
        ...savedCompletedSteps
      }));
      setIsSidebarTour(true);
    }
  }, []);

  useEffect(() => {
    if (isSidebarTour) {
      const tourState = {
        currentItemIndex: currentSidebarItem,
        completedSteps: {
          tour: completedSteps.tour,
          help: completedSteps.help
        }
      };
      localStorage.setItem('cashmate_tour_state', JSON.stringify(tourState));
    } else {
      localStorage.removeItem('cashmate_tour_state');
    }
  }, [isSidebarTour, currentSidebarItem, completedSteps.tour, completedSteps.help]);

  const handleDismiss = () => {
    setDismissed(true);
    setShowGuide(false);
    // Store dismissal in localStorage
    localStorage.setItem('onboardingDismissed', 'true');
  };

  const markOnboardingCompleted = async () => {
    if (!user?.uid) return;
    
    try {
      // Update Firestore
      await updateDoc(doc(db, 'users', user.uid), {
        onboardingCompleted: true,
        onboardingCompletedAt: new Date().toISOString()
      });
      
      // Update local state
      setShowWelcome(false);
      setShowGuide(false);
      setIsSidebarTour(false);
      setDismissed(true);
      
    } catch (error) {
      console.error('Error marking onboarding as completed:', error);
    }
  };

  const firstIncompleteIndex = steps.findIndex(step => !completedSteps[step.key]);
  const progress = firstIncompleteIndex === -1 ? 100 : (firstIncompleteIndex / steps.length) * 100;

  return (
    <>
      {isSidebarTour && (
        <div 
          className="fixed inset-0 z-40 pointer-events-none"
          style={{
            background: 'rgba(0,0,0,0.5)',
            pointerEvents: 'auto',
            zIndex: 9998
          }}
        >
          {renderSidebarTour()}
        </div>
      )}
      
      {showWelcome && (
        <AnimatePresence>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-gray-800 rounded-xl p-8 max-w-md w-full mx-4">
              <div className="text-center">
                <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">👋</span>
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Welcome to CashMate!</h2>
                <p className="text-gray-300 mb-8">
                  Hey there! looks like you're new here. Let's get you set up with a quick onboarding process to help you get started.
                </p>
                <button
                  onClick={handleWelcomeNext}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
                >
                  Get Started
                </button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      )}

      {showGuide && !isSidebarTour && (
        <AnimatePresence>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 z-50 w-96"
          >
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl shadow-2xl overflow-hidden border border-gray-700">
              {/* Header */}
              <div className="p-5 bg-gray-800 border-b border-gray-700">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white">Getting Started</h3>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4 w-full bg-gray-700 rounded-full h-2">
                  <motion.div 
                    className="h-full bg-purple-500 rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>

              {/* Current Step */}
              <div className="p-5">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white text-xl">
                    {steps[currentStep].icon}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-white">{steps[currentStep].title}</h4>
                    <p className="text-sm text-gray-300 mt-1">{steps[currentStep].description}</p>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="px-5 py-4 bg-gray-800 border-t border-gray-700">
                <div className="flex justify-between">
                  <div className="flex space-x-2">
                    {currentStep > 0 && (
                      <button
                        onClick={() => setCurrentStep(p => Math.max(0, p - 1))}
                        className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white"
                      >
                        Back
                      </button>
                    )}
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleCompleteStep(steps[currentStep])}
                      className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
                      disabled={isValidatingStep}
                    >
                      {isValidatingStep ? 'Checking...' : steps[currentStep].key === 'help' ? 'Got it!' : 'Continue'}
                    </button>
                  </div>
                </div>
                {validationError && (
                  <p className="mt-3 text-sm text-red-400">
                    {validationError}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </>
  );
}
